import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency } from '@/lib/format';
import type { ClassInstallment } from '@/types/fees';
import type { InstituteClass } from '@/types/instituteClass';
import { feesApi } from '../api/fees.api';

/** Mirrors `MAX_INSTALLMENTS` in the API's fees validation. */
const MAX_INSTALLMENTS = 12;

interface DraftRow {
  /** Stable across edits, for React's list keys. Never sent. */
  key: string;
  name: string;
  dueDate: string;
  /** As typed: whole rupees. */
  amount: string;
}

let draftCounter = 0;
const draftKey = () => `draft-${(draftCounter += 1)}`;

function toDraft(installments: ClassInstallment[], classTotal: number): DraftRow[] {
  if (installments.length === 0) {
    // A class with no schedule starts on one installment holding the whole fee,
    // which is what it is today; splitting it is one "Add installment" away.
    return [{ key: draftKey(), name: 'Term 1', dueDate: '', amount: String(classTotal) }];
  }
  return installments.map((installment) => ({
    key: draftKey(),
    name: installment.name,
    dueDate: installment.dueDate,
    amount: String(Number(installment.amount)),
  }));
}

export interface ClassInstallmentsModalProps {
  /** The class whose schedule is open. Null keeps the dialog closed. */
  instituteClass: InstituteClass | null;
  /** What that class has now — empty when it has no schedule. */
  installments: ClassInstallment[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Splits one class's fee for the year into the installments it is collected in.
 *
 * The amounts must add up to the class's total fee before it can be saved, and
 * the running total says how far off it is while typing. The server checks the
 * same thing and the dates against the session (CLAUDE.md §6). Every student in
 * the class follows the schedule: their fee account shows each installment, and
 * the register's "due" date becomes the next one they have not paid.
 */
export function ClassInstallmentsModal({
  instituteClass,
  installments,
  onClose,
  onSaved,
}: ClassInstallmentsModalProps) {
  const classTotal = Number(instituteClass?.totalFee ?? 0);
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Re-seeded whenever a different class is opened.
  useEffect(() => {
    if (!instituteClass) return;
    setRows(toDraft(installments, Number(instituteClass.totalFee)));
    setFormError(null);
    // `installments` is read for the class being opened, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instituteClass?.uuid]);

  const scheduled = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const difference = classTotal - scheduled;
  const names = rows.map((row) => row.name.trim().toLowerCase());

  function rowError(row: DraftRow, index: number): string | undefined {
    if (!row.name.trim()) return 'Name it';
    if (names.indexOf(row.name.trim().toLowerCase()) !== index) return 'Two installments have this name';
    if (!row.dueDate) return 'Choose the date it falls due';
    if (!/^\d+$/.test(row.amount) || Number(row.amount) < 1) return 'Whole rupees, more than zero';
    return undefined;
  }

  const hasRowErrors = rows.some((row, index) => rowError(row, index));
  const canSave = rows.length > 0 && !hasRowErrors && difference === 0;

  function update(key: string, patch: Partial<DraftRow>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  async function save(next: DraftRow[]) {
    if (!instituteClass) return;
    setIsSaving(true);
    setFormError(null);
    try {
      await feesApi.setInstallments(
        instituteClass.uuid,
        next.map((row) => ({ name: row.name.trim(), dueDate: row.dueDate, amount: Number(row.amount) })),
      );
      toast.success(
        next.length > 0 ? `${instituteClass.name}'s installments saved` : `${instituteClass.name}'s installments removed`,
        next.length > 0
          ? `${next.length} ${next.length === 1 ? 'installment' : 'installments'} for every student in the class.`
          : 'The class is back to one amount for the year.',
      );
      onSaved();
    } catch (caught) {
      setFormError(caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={Boolean(instituteClass)}
      onClose={onClose}
      size="lg"
      title={instituteClass ? `Installments for ${instituteClass.name}` : 'Installments'}
      description={`Split ${formatCurrency(classTotal)}, the class's fee for the year, into the dates it is collected on. A concession comes off the last installments.`}
      footer={
        <>
          {installments.length > 0 && (
            <Button
              variant="dangerSoft"
              disabled={isSaving}
              onClick={() => void save([])}
              className="mr-auto"
            >
              Remove installments
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void save(rows)} isLoading={isSaving} disabled={!canSave}>
            {isSaving ? 'Saving…' : 'Save installments'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {formError && (
          <Alert tone="critical" title="Could not save these installments">
            {formError}
          </Alert>
        )}

        {classTotal <= 0 && (
          <Alert tone="warning" title="This class has no fee set">
            Set the class's fee on the Classes page first, then split it into installments.
          </Alert>
        )}

        <ol className="flex flex-col gap-3">
          {rows.map((row, index) => {
            const error = rowError(row, index);
            return (
              <li key={row.key} className="card flex flex-col gap-3 p-3 sm:flex-row sm:items-start">
                <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
                  <Input
                    label="Name"
                    value={row.name}
                    maxLength={60}
                    onChange={(event) => update(row.key, { name: event.target.value })}
                  />
                  <DatePicker
                    label="Due on"
                    value={row.dueDate}
                    onChange={(event) => update(row.key, { dueDate: event.target.value })}
                  />
                  <Input
                    label="Amount (₹)"
                    inputMode="numeric"
                    value={row.amount}
                    onChange={(event) => update(row.key, { amount: event.target.value.replace(/[^\d]/g, '') })}
                  />
                  {error && (
                    <p role="alert" className="text-sm text-crit sm:col-span-3">
                      {error}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={`Remove ${row.name || `installment ${index + 1}`}`}
                  disabled={rows.length === 1}
                  onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}
                  className="self-end sm:mt-7 sm:self-start"
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </li>
            );
          })}
        </ol>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            variant="secondary"
            leftIcon={<Plus className="h-4 w-4" />}
            disabled={rows.length >= MAX_INSTALLMENTS}
            onClick={() =>
              setRows((current) => [
                ...current,
                // The new row takes whatever is still unscheduled, so the
                // total balances the moment a date is chosen.
                {
                  key: draftKey(),
                  name: `Term ${current.length + 1}`,
                  dueDate: '',
                  amount: String(Math.max(0, difference)),
                },
              ])
            }
          >
            Add installment
          </Button>

          <p className="text-sm text-ink-2" aria-live="polite">
            {formatCurrency(scheduled)} of {formatCurrency(classTotal)}
            {difference === 0 ? (
              <span className="text-ok"> · adds up</span>
            ) : difference > 0 ? (
              <span className="text-crit"> · {formatCurrency(difference)} still to place</span>
            ) : (
              <span className="text-crit"> · {formatCurrency(-difference)} too much</span>
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
}
